/**
 * DataLayer Utilities
 *
 * Type-safe utilities for pushing events to Google Tag Manager's dataLayer.
 */

import { featureFlags } from '../featureFlags';
import type { DataLayerEvent } from '../../types/analytics';

/**
 * Initialize the dataLayer array if it doesn't exist.
 * This should be called early in the app lifecycle.
 */
export function initializeDataLayer(): void {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
  }
}

/**
 * Push an event to the GTM dataLayer.
 * Events are only pushed if analytics feature flag is enabled.
 *
 * @param event - The event object to push to dataLayer
 */
export function pushToDataLayer<T extends DataLayerEvent>(event: T): void {
  if (typeof window === 'undefined') return;

  // Check feature flag - don't send events if analytics is disabled
  if (!featureFlags.analytics) {
    if (import.meta.env.DEV) {
      console.log('[Analytics] Event suppressed (analytics disabled):', event);
    }
    return;
  }

  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);

  // Log events in development for debugging
  if (import.meta.env.DEV) {
    console.log('[Analytics] DataLayer push:', event);
  }
}

/**
 * Clear the dataLayer array.
 * Useful for consent management or testing scenarios.
 */
export function clearDataLayer(): void {
  if (typeof window !== 'undefined') {
    window.dataLayer = [];
  }
}
